// src/services/ticketsService.ts
//
// All Supabase access for the ticket-desk read surface (Story 4.1) is isolated
// here. This service is READ-ONLY BY DESIGN: yak_tickets is default-deny for
// client writes (3.5 AC 2 — a GUC-guarded trigger rejects direct writes), and
// every ticket lifecycle mutation (respond / close / escalate / reassign /
// correct) is a `tx_` SECURITY DEFINER RPC owned by later Epic-4 stories
// (4.3/4.4/4.5), never a method here. Reads run as direct SELECTs under the
// `_org_isolation` RLS policy Story 3.5 shipped — RLS scopes rows to the
// caller's org, so no explicit organization_id filter is needed (the `mine`
// view additionally needs the session user id).
//
// Layering (spine AD-3): components/hooks never touch Supabase; they consume the
// clean `src/types/yak.ts` shapes. Results are cast once per method at the
// boundary. Reference chain: regionsService → useRegions → RegionManagement.

import { supabase } from '../lib/supabase'
import type {
  YakTicketWithAssignee,
  YakTicketViewKey,
  YakTicketFilters,
  YakTicketPage,
  YakCallWithRep,
  YakSiblingTicket,
  YakAssignmentWithUser,
  YakKbPinWithDocument,
  YakMessageWithAuthor,
  YakCorrectionWithAuthor,
  YakRelatedTicket,
  YakRoutingRule,
} from '../types/yak'
import {
  TICKETS_PAGE_SIZE,
  ageCutoffIso,
  escapeLike,
} from '../utils/ticketQuery'

// ---------------------------------------------------------------------------
// unwrapTx — the console's SINGLE tx_ envelope idiom (AD-15). Every
// console-facing transition returns { ok, code, data }; this helper is the ONE
// place that turns it into either the data payload or a thrown HUMAN message:
//   * a transport error (network / PostgREST) → the generic retryable copy
//   * ok:false → the mapped message for the enumerated business code
//   * unknown code → the generic retryable copy
// Never let raw Postgres/PostgREST text reach the UI (1.1's throwMapped lesson
// applied to the AD-15 envelope). Story 4.2 ships the first caller
// (tx_link_tickets); 4.3/4.4/4.5 import this helper and extend TX_CODE_MESSAGES
// with their own codes — do NOT fork a second unwrapper.
// ---------------------------------------------------------------------------

export interface TxEnvelope {
  ok: boolean
  code: string
  data?: unknown
}

interface RpcResultLike {
  data: unknown
  error: { message: string } | null
}

const GENERIC_TX_ERROR = 'Something went wrong. Please try again.'

// Enumerated business-code → human copy. Extended by later Epic-4 stories.
// NOTE on `not_assignee`: 4.3 mapped it for respond/note/close ("reassigned while
// you were writing"). Escalate needs a DIFFERENT message ("reassign to yourself
// first"), so `escalate()` maps its own not_assignee copy per-call rather than
// letting one key carry two meanings (the never-two-divergent-meanings rule).
export const TX_CODE_MESSAGES: Record<string, string> = {
  not_found: "That ticket doesn't exist or was removed — refresh and try again",
  self_link: "A ticket can't be linked to itself",
  already_linked: 'These tickets are already linked',
  // Story 4.3 (respond / note / close) codes.
  not_assignee:
    'This ticket was reassigned while you were writing — review before sending.',
  invalid_status: 'This ticket is closed — a reply from the rep reopens it.',
  empty_body: 'Enter a message before sending.',
  already_closed: 'This ticket is already closed.',
  // Story 4.4 (escalate / reassign) codes.
  terminal_layer:
    'This ticket is already at the final escalation layer for its question type.',
  not_open: 'Escalation applies to open tickets.',
  already_assignee: 'That team member is already the assignee.',
  target_inactive:
    'That team member is deactivated — choose an active team member.',
  // Story 4.5 (correct field) codes. `not_assignee` is NOT added here — it already
  // carries the respond meaning; correctField() maps its own per-call copy below
  // (the never-two-divergent-meanings-under-one-key rule, per escalate).
  invalid_field: "That field can't be corrected on this ticket type.",
  invalid_value:
    'That region no longer exists in your organization — refresh and pick again.',
  no_change: 'The field already has that value — nothing to record.',
}

// The per-call not_assignee copy for escalate (distinct from 4.3's respond-side
// meaning — see the TX_CODE_MESSAGES note).
const ESCALATE_NOT_ASSIGNEE =
  'Only the current assignee can escalate — reassign the ticket to yourself first.'

// The per-call not_assignee copy for correctField (points at the Reassign remedy,
// like escalate — distinct from 4.3's respond-side "reassigned while writing").
const CORRECT_NOT_ASSIGNEE =
  'Only the current assignee can correct fields — reassign the ticket to yourself first.'

export function unwrapTx<T = unknown>(result: RpcResultLike): T {
  // Transport failure (network down, function missing, auth): generic retryable.
  if (result.error) throw new Error(GENERIC_TX_ERROR)
  const env = result.data as TxEnvelope | null
  if (!env || env.ok !== true) {
    const code = env?.code ?? ''
    throw new Error(TX_CODE_MESSAGES[code] ?? GENERIC_TX_ERROR)
  }
  return env.data as T
}

// The assignee embed rides the composite FK yak_tickets_assignee_fkey
// (assignee_user_id, organization_id) → user_profiles (id, organization_id),
// reconciled from the landed 3.5 migration + generated types. Naming the
// constraint disambiguates it from the same-named view relationship. The embed
// reads under user_profiles' `user_profiles_same_org_read` SELECT policy.
const TICKET_SELECT =
  '*, assignee:user_profiles!yak_tickets_assignee_fkey(id, first_name, last_name, email)'

class TicketsService {
  // One page of a view. Single SELECT with an exact count and a server-side
  // range so the header count and pagination reflect the FILTERED total (not
  // the loaded page). Ordering: open views oldest-first (aging surfaces at the
  // top — FR-22), Closed newest-first (the record; recency wins); id tiebreak
  // keeps pages stable. All predicates apply server-side.
  async listTickets(
    view: YakTicketViewKey,
    filters: YakTicketFilters,
    page: number
  ): Promise<YakTicketPage> {
    const safePage = page < 1 ? 1 : page
    const from = (safePage - 1) * TICKETS_PAGE_SIZE
    const to = safePage * TICKETS_PAGE_SIZE - 1

    let query = supabase
      .from('yak_tickets')
      .select(TICKET_SELECT, { count: 'exact' })

    // Story 6.3 review: a reference code identifies exactly ONE ticket, so the
    // view is meaningless for it — ANDing the two meant a rep quoting a code for
    // an open ticket got zero results whenever the operator happened to be on
    // Closed (the tab TicketFilterBar's own header calls where the box "matters
    // most"), and "Mine" hid a colleague's ticket entirely. The operator had no
    // signal the code matched anywhere, so the only recovery was retrying all
    // three tabs. A code lookup now bypasses the view predicate; RLS still scopes
    // to the org, which is the boundary that actually matters.
    const referenceLookup = (filters.reference ?? '').trim().length > 0

    // View predicate (status set + optional assignee scope).
    if (referenceLookup) {
      // No status or assignee predicate — the code is the whole query.
    } else if (view === 'mine') {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const userId = session?.user?.id ?? null
      if (!userId) {
        // No session → no "mine". Return an empty page rather than leaking the
        // whole org (RLS would still scope, but the assignee predicate is the
        // point of this view).
        return {
          tickets: [],
          total: 0,
          page: safePage,
          pageSize: TICKETS_PAGE_SIZE,
        }
      }
      query = query
        .in('status', ['open', 'answered'])
        .eq('assignee_user_id', userId)
    } else if (view === 'all_open') {
      query = query.in('status', ['open', 'answered'])
    } else {
      query = query.eq('status', 'closed')
    }

    // Combinable filters (each optional; all server-side).
    if (filters.questionType) {
      query = query.eq('question_type', filters.questionType)
    }
    if (filters.brand) {
      // brand lives top-level inside the canonical structured_fields jsonb
      // (AD-14) — PostgREST ->> arrow projection.
      query = query.eq('structured_fields->>brand', filters.brand)
    }
    if (filters.routeLabel) {
      query = query.eq('route_label', filters.routeLabel)
    }
    if (filters.age) {
      const cutoff = ageCutoffIso(filters.age, new Date())
      if (cutoff) query = query.lt('created_at', cutoff)
    }
    if (referenceLookup) {
      // Story 6.3 / FR-43: the rep quotes a WHOLE code ("call back about
      // YAK-20260802-014"), so this is an exact match, not a substring scan —
      // `ilike` with no wildcards is exactly that, and stays case-insensitive so
      // the operator needn't reproduce the org prefix's casing. `%`, `_` and `*`
      // are escaped: codes contain none of them, but this is free text and an
      // unescaped metacharacter would silently widen the filter into a wildcard
      // scan (`*` is PostgREST's alias for `%` — see escapeLike).
      query = query.ilike(
        'reference_code',
        escapeLike((filters.reference ?? '').trim())
      )
    }

    // Ordering by view, then the stable id tiebreak.
    const ascending = view !== 'closed'
    query = query
      .order('created_at', { ascending })
      .order('id', { ascending: true })
      .range(from, to)

    const { data, error, count } = await query
    if (error) throw new Error(error.message)

    return {
      tickets: (data as unknown as YakTicketWithAssignee[] | null) ?? [],
      total: count ?? 0,
      page: safePage,
      pageSize: TICKETS_PAGE_SIZE,
    }
  }

  // Single ticket for the detail route (the 3.7 email deep-link target). Same
  // assignee embed. A 0-row outcome (bad id, or out-of-org under RLS) resolves
  // to null — not thrown — so the detail page can render its not-found state.
  async getTicket(id: string): Promise<YakTicketWithAssignee | null> {
    const { data, error } = await supabase
      .from('yak_tickets')
      .select(TICKET_SELECT)
      .eq('id', id)
      .maybeSingle()

    // maybeSingle returns null data with no error on 0 rows; guard PGRST116
    // defensively in case a single()-style path ever surfaces it.
    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(error.message)
    }
    return (data as unknown as YakTicketWithAssignee | null) ?? null
  }

  // -------------------------------------------------------------------------
  // Detail reads (Story 4.2). All direct SELECTs under the _org_isolation RLS
  // policy — RLS scopes rows to the caller's org, so no explicit
  // organization_id filter is needed. Each casts once at the boundary; single-
  // row reads map a 0-row / PGRST116 outcome to null.
  // -------------------------------------------------------------------------

  // The call behind a ticket, with its rep (caller) embedded via the composite
  // FK yak_calls_rep_fkey. rep is null for an unrecognized caller (rep_id NULL).
  async getCall(callId: string): Promise<YakCallWithRep | null> {
    const { data, error } = await supabase
      .from('yak_calls')
      .select('*, rep:yak_reps!yak_calls_rep_fkey(id, name, email)')
      .eq('id', callId)
      .maybeSingle()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(error.message)
    }
    return (data as unknown as YakCallWithRep | null) ?? null
  }

  // Every ticket the call spawned (INCLUDING self), for the "This call asked N
  // questions" sibling list. created_at asc so the list reads in call order.
  async listCallTickets(callId: string): Promise<YakSiblingTicket[]> {
    const { data, error } = await supabase
      .from('yak_tickets')
      .select('id, question, status, route_label')
      .eq('call_id', callId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakSiblingTicket[] | null) ?? []
  }

  // Assignment history with each assignee's name + position. DISPLAY order is
  // created_at asc (a timeline reads forward); 3.5's "latest = created_at desc,
  // tiebreak id" rule stays the semantic contract for 4.4's escalate read.
  async listAssignments(ticketId: string): Promise<YakAssignmentWithUser[]> {
    const { data, error } = await supabase
      .from('yak_ticket_assignments')
      .select(
        '*, assignee:user_profiles!yak_ticket_assignments_assignee_fkey(id, first_name, last_name, email, position)'
      )
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakAssignmentWithUser[] | null) ?? []
  }

  // KB pins joined through their version to the owning document (title/status/
  // version number). Retired documents keep versions readable, so pins never
  // dangle (2.2). Flattened to the panel's YakKbPinWithDocument shape.
  async listKbPins(ticketId: string): Promise<YakKbPinWithDocument[]> {
    const { data, error } = await supabase
      .from('yak_ticket_kb_pins')
      .select(
        'id, document_version_id, similarity, version:yak_kb_doc_versions!yak_ticket_kb_pins_version_fkey(version_number, document_id, document:yak_kb_documents(id, title, status))'
      )
      .eq('ticket_id', ticketId)

    if (error) throw new Error(error.message)

    interface PinRow {
      id: string
      document_version_id: string
      similarity: number | null
      version: {
        version_number: number
        document_id: string
        document: {
          id: string
          title: string
          status: 'active' | 'retired'
        } | null
      } | null
    }
    const rows = (data as unknown as PinRow[] | null) ?? []
    return rows.map(p => ({
      id: p.id,
      document_version_id: p.document_version_id,
      similarity: p.similarity,
      document_id: p.version?.document?.id ?? p.version?.document_id ?? '',
      version_number: p.version?.version_number ?? 0,
      title: p.version?.document?.title ?? 'Untitled document',
      status: p.version?.document?.status ?? 'active',
    }))
  }

  // The typed thread, chronologically oldest-first (id tiebreak for stability),
  // with the console author's name embedded (null on rep replies).
  async listMessages(ticketId: string): Promise<YakMessageWithAuthor[]> {
    const { data, error } = await supabase
      .from('yak_ticket_messages')
      .select(
        '*, author:user_profiles!yak_ticket_messages_author_fkey(id, first_name, last_name, email)'
      )
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
      .order('id', { ascending: true })

    if (error) throw new Error(error.message)
    return (data as unknown as YakMessageWithAuthor[] | null) ?? []
  }

  // The field-correction trail, newest-first (the panel groups by field), with
  // the corrector's name embedded via the composite FK.
  async listCorrections(ticketId: string): Promise<YakCorrectionWithAuthor[]> {
    const { data, error } = await supabase
      .from('yak_field_corrections')
      .select(
        '*, author:user_profiles!yak_field_corrections_author_fkey(id, first_name, last_name, email)'
      )
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })

    if (error) throw new Error(error.message)
    return (data as unknown as YakCorrectionWithAuthor[] | null) ?? []
  }

  // Related tickets: the link pair is queryable from EITHER column, so match on
  // both, then fetch the counterpart tickets. Returns the "other" ticket in each
  // pair (question + status + created date).
  async listLinks(ticketId: string): Promise<YakRelatedTicket[]> {
    const { data: links, error } = await supabase
      .from('yak_ticket_links')
      .select('ticket_id, related_ticket_id')
      .or(`ticket_id.eq.${ticketId},related_ticket_id.eq.${ticketId}`)

    if (error) throw new Error(error.message)
    const rows =
      (links as unknown as
        | {
            ticket_id: string
            related_ticket_id: string
          }[]
        | null) ?? []

    const counterpartIds = Array.from(
      new Set(
        rows.map(r =>
          r.ticket_id === ticketId ? r.related_ticket_id : r.ticket_id
        )
      )
    )
    if (counterpartIds.length === 0) return []

    const { data: tickets, error: tErr } = await supabase
      .from('yak_tickets')
      .select('id, question, status, created_at')
      .in('id', counterpartIds)
      .order('created_at', { ascending: false })

    if (tErr) throw new Error(tErr.message)
    return (tickets as unknown as YakRelatedTicket[] | null) ?? []
  }

  // Question-text search for the link picker (org-scoped via RLS). Excludes the
  // current ticket and already-linked ones server-side; caps at 10.
  async searchTicketsForLink(
    query: string,
    excludeIds: string[]
  ): Promise<YakRelatedTicket[]> {
    const trimmed = query.trim()
    if (!trimmed) return []

    let q = supabase
      .from('yak_tickets')
      .select('id, question, status, created_at')
      .ilike('question', `%${trimmed}%`)

    if (excludeIds.length > 0) {
      q = q.not('id', 'in', `(${excludeIds.join(',')})`)
    }
    q = q.order('created_at', { ascending: false }).limit(10)

    const { data, error } = await q
    if (error) throw new Error(error.message)
    return (data as unknown as YakRelatedTicket[] | null) ?? []
  }

  // The routing rule for a question type (SELECT open to all org members — 1.5).
  // The detail view resolves an assignment's layer_id → position/index against
  // this rule's layers jsonb, tolerating staleness.
  async getRulesRowForType(
    questionType: string
  ): Promise<YakRoutingRule | null> {
    const { data, error } = await supabase
      .from('yak_routing_rules')
      .select('*')
      .eq('question_type', questionType)
      .maybeSingle()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(error.message)
    }
    return (data as unknown as YakRoutingRule | null) ?? null
  }

  // The ONLY mutation in Story 4.2: link two tickets via the tx_link_tickets
  // AD-15 transition (granted to authenticated). The actor id is the session
  // user; yak_validate_actor cross-checks it against auth.uid() server-side.
  // unwrapTx maps the business codes (not_found | self_link | already_linked)
  // to human copy and any transport error to the generic retryable message.
  async linkTickets(
    ticketId: string,
    relatedTicketId: string
  ): Promise<{ link_id: string }> {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    const userId = session?.user?.id ?? null
    if (!userId) {
      throw new Error(
        'Your session has expired — sign in again to link tickets'
      )
    }

    const result = await supabase.rpc('tx_link_tickets', {
      p_ticket_id: ticketId,
      p_related_ticket_id: relatedTicketId,
      p_actor: { kind: 'user', id: userId },
    })
    return unwrapTx<{ link_id: string }>(result)
  }

  // -------------------------------------------------------------------------
  // Story 4.3 write actions — respond / note / close. Each is a `tx_`
  // SECURITY DEFINER transition granted to `authenticated`, reached ONLY via
  // .rpc() and unwrapped by unwrapTx (AD-3/AD-15 — zero direct table writes).
  // The actor is {kind:'user', id:<session user>}; yak_validate_actor
  // cross-checks it against auth.uid() server-side (the human click IS the
  // approval). sent_unedited is computed in the DB (AD-8) — never sent up.
  // -------------------------------------------------------------------------

  // The session user id, or a thrown human message when the session expired.
  private async requireUserId(action: string): Promise<string> {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    const userId = session?.user?.id ?? null
    if (!userId) {
      throw new Error(`Your session has expired — sign in again to ${action}`)
    }
    return userId
  }

  // Send a response: message + status→answered + event + notifications, one
  // atomic transition. Returns the new message id, the new status, and the
  // server-computed sent_unedited (the record; the UI never recomputes it).
  async respond(
    ticketId: string,
    body: string
  ): Promise<{
    message_id: string
    status: string
    sent_unedited: boolean | null
  }> {
    const userId = await this.requireUserId('respond')
    const result = await supabase.rpc('tx_respond', {
      p_ticket_id: ticketId,
      p_body: body,
      p_actor: { kind: 'user', id: userId },
    })
    return unwrapTx<{
      message_id: string
      status: string
      sent_unedited: boolean | null
    }>(result)
  }

  // Add an internal note: one console-only thread row, no status change, no
  // notifications (structurally un-emailable — tx_note mints nothing outward).
  async note(ticketId: string, body: string): Promise<{ message_id: string }> {
    const userId = await this.requireUserId('add a note')
    const result = await supabase.rpc('tx_note', {
      p_ticket_id: ticketId,
      p_body: body,
      p_actor: { kind: 'user', id: userId },
    })
    return unwrapTx<{ message_id: string }>(result)
  }

  // Close the ticket (manual — p_cause defaults to 'manual'; the 'auto' cron
  // shape is 4.7's, never the console's). No notifications minted.
  async close(ticketId: string): Promise<{ status: string }> {
    const userId = await this.requireUserId('close this ticket')
    const result = await supabase.rpc('tx_close', {
      p_ticket_id: ticketId,
      p_actor: { kind: 'user', id: userId },
    })
    return unwrapTx<{ status: string }>(result)
  }

  // -------------------------------------------------------------------------
  // Story 4.4 write actions — escalate / reassign. Each is a `tx_` SECURITY
  // DEFINER transition granted to `authenticated`, reached ONLY via .rpc() and
  // unwrapped by unwrapTx. Escalation is human-driven reassignment along the
  // org's rules — the resolution engine runs SERVER-side (AD-2); the console
  // never predicts the resolved individual. No optimistic state — the screen
  // re-renders from the server's answer via the refetch.
  // -------------------------------------------------------------------------

  // Escalate onward from the ticket's current layer, optionally attaching an
  // internal note (one gesture). Returns the new assignee (the screen refetches
  // to render it). not_assignee is mapped to escalate-specific copy per-call.
  async escalate(
    ticketId: string,
    note: string | null
  ): Promise<{
    assignee_user_id: string
    layer_id: string | null
    note_message_id: string | null
  }> {
    const userId = await this.requireUserId('escalate this ticket')
    const result = await supabase.rpc('tx_escalate', {
      p_ticket_id: ticketId,
      p_actor: { kind: 'user', id: userId },
      // Omit when blank so the DB default (NULL, no note) applies — the generated
      // Args type is `p_note?: string` (optional), not nullable.
      p_note: note ?? undefined,
    })
    // Escalate's not_assignee means something different from respond's — map it
    // here before unwrapTx reaches for the shared (respond-flavoured) copy.
    if (!result.error) {
      const env = result.data as TxEnvelope | null
      if (env && env.ok !== true && env.code === 'not_assignee') {
        throw new Error(ESCALATE_NOT_ASSIGNEE)
      }
    }
    return unwrapTx<{
      assignee_user_id: string
      layer_id: string | null
      note_message_id: string | null
    }>(result)
  }

  // Manually reassign the ticket to an active org console user (any MA user may —
  // FR-36; reassign-to-self is legal). The resolution engine is NOT called.
  async reassign(
    ticketId: string,
    targetUserId: string
  ): Promise<{ assignee_user_id: string }> {
    const userId = await this.requireUserId('reassign this ticket')
    const result = await supabase.rpc('tx_reassign', {
      p_ticket_id: ticketId,
      p_assignee_user_id: targetUserId,
      p_actor: { kind: 'user', id: userId },
    })
    return unwrapTx<{ assignee_user_id: string }>(result)
  }

  // -------------------------------------------------------------------------
  // Story 4.5 write action — correct one structured field. A `tx_` SECURITY
  // DEFINER transition granted to `authenticated`, reached ONLY via .rpc() and
  // unwrapped by unwrapTx (AD-3/AD-15 — zero direct table writes; structured_fields
  // is default-deny for client writes). `field` is a canonical top-level key
  // (brand|region_id|channel|payer) or a dotted extras.<key> path; `newValue` is
  // the corrected text, or NULL to CLEAR the field. The correction is recorded
  // append-only and NEVER re-runs assignment (FR-37 — reassignment is the remedy).
  // No optimistic state: the caller refetches and re-renders from the server.
  // -------------------------------------------------------------------------
  async correctField(
    ticketId: string,
    field: string,
    newValue: string | null
  ): Promise<{
    ticket_id: string
    correction_id: string
    field: string
    old_value: string | null
    new_value: string | null
    structured_fields: Record<string, unknown>
  }> {
    const userId = await this.requireUserId('correct fields')
    const result = await supabase.rpc('tx_correct_field', {
      p_ticket_id: ticketId,
      p_field: field,
      // NULL = clear; the generated Args type is non-nullable string, but
      // PostgREST forwards a JSON null as SQL NULL (tx_correct_field normalizes it
      // to a clear). Cast to satisfy the generated signature.
      p_new_value: newValue as string,
      p_actor: { kind: 'user', id: userId },
    })
    // correctField's not_assignee points at Reassign — map it before unwrapTx
    // reaches for the shared (respond-flavoured) copy (4.4's keying lesson).
    if (!result.error) {
      const env = result.data as TxEnvelope | null
      if (env && env.ok !== true && env.code === 'not_assignee') {
        throw new Error(CORRECT_NOT_ASSIGNEE)
      }
    }
    return unwrapTx<{
      ticket_id: string
      correction_id: string
      field: string
      old_value: string | null
      new_value: string | null
      structured_fields: Record<string, unknown>
    }>(result)
  }

  // Distinct non-null brands across the org's tickets, for the filter select.
  // The filterable universe is what tickets actually carry (Decision 7) — not
  // KB tags. Full-column projection is accepted at pilot volume; dedupe + sort
  // client-side. Revisit with Epic 5 analytics if ticket volume grows.
  async listBrandOptions(): Promise<string[]> {
    const { data, error } = await supabase
      .from('yak_tickets')
      .select('brand:structured_fields->>brand')
      .not('structured_fields->>brand', 'is', null)

    if (error) throw new Error(error.message)
    const rows = (data as unknown as { brand: string | null }[] | null) ?? []
    const brands = new Set<string>()
    for (const row of rows) {
      const b = row.brand
      if (typeof b === 'string' && b.trim()) brands.add(b)
    }
    return Array.from(brands).sort((a, b) => a.localeCompare(b))
  }
}

export const ticketsService = new TicketsService()
